import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import {
  Eye,
  EyeOff,
  Building,
  User,
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  Check,
  Mail,
  Calendar,
  Shield,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  UserCheck,
  Search,
  Share2,
  Users,
  FileText,
  Link2,
  Clock,
} from "lucide-react";
import { Icon } from "@iconify/react"; // npm i @iconify/react
import InputField from "./components/InputComponent/InputField";
import axios from "axios";
import ilaLogo from "./assets/ila-logo.png";
import ourDeptLogo from "./assets/ourdept-logo.png";
import "./Login.scss";

/* Products shown in the top launcher come from
   GET {BACKEND}/api/public/products/login-page/all
   Response: { status, message, data: [{ productName, description, productCode,
   productLink, productIconUrl, productType }] }
   The API has no colour fields, so each product gets a stable accent colour
   derived from its productCode. Works for any number of products. */
const PRODUCTS_URL = "/api/public/products/login-page/all";

const hashString = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
};

// Golden-angle hue spacing keeps neighbouring products visually distinct.
const productColors = (key) => {
  const hue = (hashString(key) * 137.508) % 360;
  return {
    color: `hsl(${hue.toFixed(0)} 65% 42%)`,
    softColor: `hsl(${hue.toFixed(0)} 85% 93%)`,
  };
};

const normalizeHost = (host = "") => host.toLowerCase().replace(/^www\./, "");

const hostOf = (url = "") => {
  try {
    return normalizeHost(new URL(url, window.location.origin).hostname);
  } catch {
    return "";
  }
};

// True when the product is the app the user is currently on. Matches either
// the product's own code (REACT_APP_PRODUCT_ID, the same value sent as
// X-ProductID on login) or its link's hostname vs. the current URL.
const isCurrentProduct = (product) => {
  const currentCode = process.env.REACT_APP_PRODUCT_ID;
  if (currentCode && product.productCode === currentCode) return true;
  const productHost = hostOf(product.productLink);
  return (
    !!productHost && productHost === normalizeHost(window.location.hostname)
  );
};

const isImageUrl = (value = "") =>
  /^(https?:)?\/\/|^\/|^data:image\//i.test(value);

function ProductIcon({ product }) {
  const { productIconUrl, productName } = product;
  if (productIconUrl && isImageUrl(productIconUrl)) {
    return (
      <img className="lg-app__img" src={productIconUrl} alt="" loading="lazy" />
    );
  }
  if (productIconUrl) {
    // Iconify id, e.g. "icon-park:setting-two"
    return <Icon icon={productIconUrl} width="22" height="22" />;
  }
  // No icon supplied: fall back to the first letter of the name.
  return (
    <span className="lg-app__letter">
      {(productName || "?").trim().charAt(0).toUpperCase()}
    </span>
  );
}

/* Small building blocks shared by the animated mock screens */
const Win = ({ title, mod, toast, children }) => (
  <div className="win">
    <div className="win__bar">
      <i />
      <i />
      <i />
      <span>{title}</span>
    </div>
    <div className={`win__body win__body--${mod}`}>
      {children}
      {toast && (
        <div className="toast">
          <Check /> {toast}
        </div>
      )}
    </div>
  </div>
);

const Stats = ({ items }) => (
  <div className="stats" style={{ "--cols": items.length }}>
    {items.map(([label, value, color], n) => (
      <div className="stat" key={label} style={{ "--c": color, "--n": n }}>
        <span>{label}</span>
        <b>{value}</b>
      </div>
    ))}
  </div>
);

const Pill = ({ color, children }) => (
  <span className="pill" style={color ? { "--c": color } : undefined}>
    {children}
  </span>
);

/* A label that cross-fades into its "done" state */
const Swap = ({ before, after, tone = "ghost" }) => (
  <span className="swap">
    <span className={`btn-mini btn-mini--${tone} swap__a`}>{before}</span>
    <span className="btn-mini btn-mini--ok swap__b">
      <Check /> {after}
    </span>
  </span>
);

export default function ModernLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("login");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    tenantId: "",
    username: "",
    password: "",
    email: "",
    dob: "",
    newPassword: "",
    confirmPassword: "",
    otp: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  /* First four: company side (HR portal). Last four: consultancy side. */
  const currentSlides = [
    {
      badge: "RECRUITMENT",
      title: "Two Portals, One Login",
      content: "Choose how you'd like to work today.",
      subtitle: "Switch between workspaces anytime from the navbar.",
      features: [
        "🏢 Company: HR, Manager and HOD workflows",
        "🤝 Consultancy: consultant and admin workflows",
        "🔐 One secure sign-in for both",
        "⚡ Jump between portals instantly",
      ],
      footer: "Hire your way — in-house or through partners.",
      visual: "workspace",
    },
    {
      badge: "HR PORTAL",
      tone: "dark",
      title: "Approve Vacancies Faster",
      content: "Final HR sign-off on every vacancy request.",
      features: [
        "📋 Track HOD and HR approvals side by side",
        "✅ See approved, pending and rejected at a glance",
        "📈 Live approval breakdown and pipeline",
        "🚀 Turn approved vacancies into job posts",
      ],
      footer: "No request left waiting.",
      visual: "approvals",
    },
    {
      badge: "HR PORTAL",
      tone: "dark",
      title: "Design Your Hiring Flow",
      content:
        "Define interview pipelines once, reuse them for every job post.",
      features: [
        "🧭 Build multi-round interview flows",
        "🎯 Online, offline or hybrid modes",
        "🔗 Link flows to your job posts",
        "📊 See rounds and coverage per flow",
      ],
      footer: "Every candidate, a consistent experience.",
      visual: "flows",
    },
    {
      badge: "HR PORTAL",
      tone: "dark",
      title: "Grow Your Partner Network",
      content:
        "Find consultancies, send invites and exchange terms in one place.",
      features: [
        "🔎 Search partner companies by name or domain",
        "✉️ Send connection invites in a click",
        "📜 Exchange Terms & Conditions",
        "🤝 Track every connection's status",
      ],
      footer: "Better partners, faster hiring.",
      visual: "connect",
    },
    {
      badge: "CONSULTANT PORTAL",
      tone: "blue",
      title: "Consultancy Dashboard",
      content: "Requirements, shared candidates and fulfilment in one view.",
      features: [
        "📊 Track fulfilment across all connections",
        "🧾 See requirements by client",
        "💼 Monitor every job post's status",
        "🔔 Know exactly where the gaps are",
      ],
      footer: "Deliver more. Miss nothing.",
      visual: "cdash",
    },
    {
      badge: "CONSULTANT PORTAL",
      tone: "blue",
      title: "Post and Publish Jobs",
      content: "Turn client requirements into published job posts.",
      features: [
        "📝 Create from a company requirement or manually",
        "📣 Publish to make roles visible",
        "🔗 Share, schedule and link posts",
        "📥 Export everything to Excel",
      ],
      footer: "From requirement to live role in minutes.",
      visual: "jobposts",
    },
    {
      badge: "CONSULTANT PORTAL",
      tone: "blue",
      title: "Share the Right Candidates",
      content: "Send candidates to client companies and track their journey.",
      features: [
        "👥 Share candidates against each requirement",
        "🔍 Search by candidate, role or job code",
        "📍 Track placed, shared and rejected status",
        "🗂 Keep a full placement history",
      ],
      footer: "Every placement, fully traceable.",
      visual: "share",
    },
    {
      badge: "CONSULTANT PORTAL",
      tone: "blue",
      title: "Stay Connected with Clients",
      content: "Respond to invites and manage terms with every client.",
      features: [
        "✉️ Accept client connection invites",
        "📜 Review and agree Terms & Conditions",
        "🟢 See active and pending connections",
        "🧩 Keep every client relationship organised",
      ],
      footer: "Strong client relationships, organised.",
      visual: "cconn",
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Product launcher: fetched once on mount.
  useEffect(() => {
    const controller = new AbortController();

    const fetchProducts = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}${PRODUCTS_URL}`,
          { signal: controller.signal },
        );
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setProducts(
          list
            .filter((p) => p && p.productName && !isCurrentProduct(p))
            .map((p) => ({
              ...p,
              ...productColors(p.productCode || p.productName),
            })),
        );
      } catch (err) {
        if (axios.isCancel(err) || err.name === "CanceledError") return;
        console.error("Failed to load products:", err.message);
        setProducts([]); // launcher simply stays hidden; login still works
      } finally {
        if (!controller.signal.aborted) setProductsLoading(false);
      }
    };

    fetchProducts();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % currentSlides.length);
    }, 7000);
    return () => clearTimeout(timer);
  }, [currentSlide, currentSlides.length]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearMessages();

    const { username, password, tenantId } = formData;

    if (!username || !password || !tenantId) {
      setError("Company ID, Username, and Password are required");
      return;
    }

    setLoading(true);

    const credentials = `${username}:${password}`;
    const token = btoa(credentials);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/employeeLogin`,
        {
          method: "POST",
          headers: {
            Authorization: `Basic ${token}`,
            "X-TenantID": tenantId,
            "X-ProductID": process.env.REACT_APP_PRODUCT_ID,
          },
          credentials: "include", // Important for httpOnly cookies
        },
      );

      if (!response.ok) {
        if (response.status === 409) {
          const jwtToken = response.headers.get("Authorization");

          // Store JWT token and tenantId directly in sessionStorage
          if (jwtToken) {
            sessionStorage.setItem("jwtToken", jwtToken);
          }

          // ✅ FIX: Store the tenantId from user input
          sessionStorage.setItem("tenantId", tenantId);

          const errorData = await response.json();
          throw new Error(errorData.message || "Conflict occurred");
        } else if (response.status === 401 || response.status === 400) {
          throw new Error("Invalid credentials");
        } else {
          throw new Error("Something went wrong. Please try again.");
        }
      }

      const data = await response.json();

      if (data) {
        // Check if JWT token is in response headers
        const jwtToken = response.headers.get("Authorization");

        // Store JWT token and tenantId directly in sessionStorage
        if (jwtToken) {
          sessionStorage.setItem("jwtToken", jwtToken);
        }

        // ✅ FIX: Store the tenantId from user input
        sessionStorage.setItem("tenantId", tenantId);

        // Set user data in AuthContext (without JWT token)
        login(data);

        setSuccess("Login successful!");

        // Navigate to the dashboard after a short delay
        setTimeout(() => {
          navigate("/dashboard");
        }, 1000);
      } else {
        setError("Invalid credentials or unexpected response.");
      }
    } catch (error) {
      console.error("Error:", error.message);
      setError(error.message || "Login failed, please try again later");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    clearMessages();

    const { email, tenantId, dob, newPassword, confirmPassword } = formData;

    if (!email || !tenantId || !dob || !newPassword || !confirmPassword) {
      setError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/forgot-password`,
        {
          email,
          currentPassword: "",
          newPassword,
          companyId: tenantId,
          dob,
        },
      );

      if (response.status === 200) {
        setSuccess(
          "Password changed successfully! Please login with your new password.",
        );
        setTimeout(() => {
          switchToLogin();
        }, 2000);
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Failed to update password. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setFormData((prev) => ({
      ...prev,
      email: "",
      dob: "",
      newPassword: "",
      confirmPassword: "",
    }));
  };

  const switchToForgotPassword = () => {
    clearMessages();
    setActiveTab("forgot");
    resetForgotPassword();
  };

  const switchToLogin = () => {
    clearMessages();
    setActiveTab("login");
    resetForgotPassword();
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % currentSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + currentSlides.length) % currentSlides.length,
    );
  };

  const renderScene = (type) => {
    switch (type) {
      case "workspace":
        return (
          <Win title="Choose your workspace" mod="ws" toast="Switch anytime">
            {[
              {
                dark: true,
                tag: "HR PORTAL",
                Ico: Briefcase,
                name: "Company",
                sub: "HR · Manager · HOD workflows",
                desc: "Manage vacancies, job posts, candidates, interviews and drives for your organisation.",
                chips: ["Vacancies", "Interviews", "Drives"],
              },
              {
                dark: false,
                tag: "CONSULTANT PORTAL",
                Ico: UserCheck,
                name: "Consultancy",
                sub: "Consultant · Admin workflows",
                desc: "Manage your consultancy's candidates, job posts, connections and client requirements.",
                chips: ["Candidates", "Job posts", "Clients"],
              },
            ].map((w, n) => (
              <div
                className={`ws__card ${w.dark ? "ws__card--dark" : ""}`}
                key={w.name}
                style={{ "--n": n }}
              >
                <span className="ws__tag">{w.tag}</span>
                <w.Ico className="ws__ico" />
                <b>{w.name}</b>
                <small>{w.sub}</small>
                <p className="ws__desc">{w.desc}</p>
                <span className="ws__chips">
                  {w.chips.map((c) => (
                    <i key={c}>{c}</i>
                  ))}
                </span>
                <ArrowRight className="ws__go" />
              </div>
            ))}
          </Win>
        );

      case "approvals":
        return (
          <Win title="HR Approvals" mod="col" toast="Vacancy approved">
            <Stats
              items={[
                ["Total requests", 14, "#2563eb"],
                ["Approved", 12, "#16a34a"],
                ["Pending", 0, "#ea580c"],
                ["Rejected", 2, "#dc2626"],
              ]}
            />
            <div className="rows">
              {[
                ["Admin", "Engineering", false],
                ["Director-Operations", "Operations", false],
                ["Manager-Accounts", "Finance", true],
              ].map(([title, dept, swap], n) => (
                <div className="rowc" key={title} style={{ "--n": n }}>
                  <div className="rowc__main">
                    <b>{title}</b>
                    <div className="rowc__chips">
                      <Pill>{dept}</Pill>
                      <Pill color="#16a34a">
                        <Check /> HOD approved
                      </Pill>
                      {swap ? (
                        <Swap
                          before="HR pending"
                          after="HR approved"
                          tone="warn"
                        />
                      ) : (
                        <Pill color="#16a34a">
                          <Check /> HR approved
                        </Pill>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="card panel">
              <div className="panel__t">HR approval breakdown</div>
              <div className="seg">
                <span style={{ "--w": "86%", "--c": "#16a34a" }} />
                <span style={{ "--w": "14%", "--c": "#dc2626" }} />
              </div>
              <small>12 approved · 0 pending · 2 rejected</small>
            </div>
          </Win>
        );

      case "flows":
        return (
          <Win title="Hiring Flows" mod="col" toast="Flow saved">
            <Stats
              items={[
                ["Total flows", 4, "#2563eb"],
                ["Interview rounds", 9, "#16a34a"],
                ["Linked job posts", 11, "#ea580c"],
              ]}
            />
            <div className="rows">
              {[
                ["3", "3 stage", 3, "Screening round", "Final round"],
                ["S", "Software Developer", 2, "Screening", "Technical"],
                ["H", "Human resource", 2, "Screening", "Communication"],
                ["H", "Human resource (Copy)", 2, "Screening", "Communication"],
              ].map(([ini, name, rounds, start, end], n) => (
                <div className="rowc" key={name} style={{ "--n": n }}>
                  <span className="rowc__av rowc__av--ring">{ini}</span>
                  <div className="rowc__main">
                    <b>{name}</b>
                    <div className="rowc__chips">
                      <Pill color="#64748b">
                        <Clock /> {rounds} rounds
                      </Pill>
                      <Pill color="#2563eb">Starts: {start}</Pill>
                      <Pill color="#16a34a">Ends: {end}</Pill>
                    </div>
                  </div>
                  <span className="dots">
                    {Array.from({ length: rounds }).map((_, d) => (
                      <i key={d} style={{ "--d": n * 0.2 + d * 0.25 }} />
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </Win>
        );

      case "connect":
        return (
          <Win title="Find Companies" mod="col" toast="3 invites sent">
            <Stats
              items={[
                ["Available companies", 11, "#2563eb"],
                ["Already connected", 4, "#16a34a"],
                ["Invites sent", 3, "#7c3aed"],
              ]}
            />
            <div className="searchbar">
              <Search /> Search by company name or domain…
            </div>
            <div className="tiles">
              {[
                ["AT", "Apex Talent Partners", "apextalent", "#4f46e5"],
                ["NS", "NorthStar Staffing", "northstar", "#0ea5e9"],
                ["BL", "BlueLeaf Consulting", "blueleaf", "#059669"],
                ["SH", "Summit HR Services", "summithr", "#ea580c"],
                ["TG", "TalentGrid Partners", "talentgrid", "#7c3aed"],
                ["CR", "CareerBridge Advisors", "careerbridge", "#dc2626"],
              ].map(([ini, name, dom, c], n) => (
                <div
                  className="card tile"
                  key={name}
                  style={{ "--n": n, "--c": c }}
                >
                  <span className="rowc__av">{ini}</span>
                  <div className="rowc__main">
                    <b>{name}</b>
                    <small>{dom}</small>
                  </div>
                  <Swap before="Invite" after="Invited" />
                </div>
              ))}
            </div>
          </Win>
        );

      case "cdash":
        return (
          <Win title="Consultancy Dashboard" mod="col" toast="Fully staffed">
            <Stats
              items={[
                ["Requirements", 7, "#2563eb"],
                ["Shared candidates", 14, "#16a34a"],
                ["Job posts", 4, "#ea580c"],
                ["Connections", 3, "#7c3aed"],
              ]}
            />
            <div className="cdash">
              <div className="card ring">
                <svg viewBox="0 0 100 100">
                  <circle className="bg" cx="50" cy="50" r="40" />
                  <circle className="fg" cx="50" cy="50" r="40" />
                </svg>
                <div className="ring__c">
                  <b>76%</b>
                  <small>fulfilled</small>
                </div>
              </div>
              <div className="card panel">
                <div className="panel__t">Requirements by connection</div>
                {[
                  ["Client #5", 4, "100%"],
                  ["Client #6", 2, "50%"],
                  ["Client #7", 1, "25%"],
                ].map(([l, v, w], n) => (
                  <div
                    className="hbar"
                    key={l}
                    style={{ "--c": "#2563eb", "--n": n, "--w": w }}
                  >
                    <span>{l}</span>
                    <div className="hbar__t">
                      <span />
                    </div>
                    <em>{v}</em>
                  </div>
                ))}
              </div>
            </div>
            <div className="rows">
              {[
                "Senior Designer",
                "Project Lead",
                "Account Manager",
                "Sales Executive",
              ].map((t, n) => (
                <div className="rowc rowc--sm" key={t} style={{ "--n": n }}>
                  <div className="rowc__main">
                    <b>{t}</b>
                  </div>
                  <Pill color="#16a34a">● Open</Pill>
                </div>
              ))}
            </div>
          </Win>
        );

      case "jobposts":
        return (
          <Win title="Job Posts" mod="col" toast="Job published">
            <Stats
              items={[
                ["Visible posts", 4, "#2563eb"],
                ["Open roles", 4, "#16a34a"],
                ["Published", 2, "#7c3aed"],
              ]}
            />
            <div className="rows">
              {[
                ["Senior Designer", "Design", 6, "3–5 years", true],
                ["Project Lead", "Operations", 2, "5+ years", false],
                ["Account Manager", "Finance", 3, "2–4 years", false],
              ].map(([t, dept, pos, exp, swap], n) => (
                <div className="card jobc" key={t} style={{ "--n": n }}>
                  <div className="jobc__top">
                    <b>{t}</b>
                    <Pill color="#16a34a">● Open</Pill>
                  </div>
                  <div className="jobc__meta">
                    🏬 {dept} · 👥 {pos} positions · 🕘 {exp}
                  </div>
                  <div className="jobc__foot">
                    {swap ? (
                      <Swap before="Publish" after="Published" />
                    ) : (
                      <span className="btn-mini btn-mini--ok">
                        <Check /> Published
                      </span>
                    )}
                    <span className="jobc__icons">
                      {[Users, Share2, Calendar, FileText, Link2].map(
                        (Ico, i) => (
                          <b key={i}>
                            <Ico />
                          </b>
                        ),
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Win>
        );

      case "share":
        return (
          <Win title="Share Candidates" mod="col" toast="Candidates shared">
            <Stats
              items={[
                ["Total shared", 14, "#2563eb"],
                ["Requirements", 2, "#16a34a"],
                ["Companies", 1, "#ea580c"],
              ]}
            />
            <div className="rows">
              {[
                ["AS", "Aarav Sharma", "Designer · JC-3065", "#0ea5e9"],
                ["DP", "Diya Patel", "Designer · JC-3065", "#16a34a"],
                ["RM", "Rohan Mehta", "Lead · JC-3068", "#6366f1"],
                ["IR", "Ishita Rao", "Lead · JC-3068", "#ec4899"],
                ["KN", "Kabir Nair", "Designer · JC-3065", "#f59e0b"],
              ].map(([ini, name, role, c], n) => (
                <div
                  className="rowc rowc--sm"
                  key={name}
                  style={{ "--n": n, "--c": c }}
                >
                  <span className="rowc__av">{ini}</span>
                  <div className="rowc__main">
                    <b>{name}</b>
                    <small>{role}</small>
                  </div>
                  <span
                    className="pill pill--late"
                    style={{ "--c": "#2563eb", "--n": n }}
                  >
                    Shared
                  </span>
                </div>
              ))}
            </div>
          </Win>
        );

      default:
        return (
          <Win title="Client Connections" mod="col" toast="Connection active">
            <Stats
              items={[
                ["Total clients", 6, "#2563eb"],
                ["Active", 5, "#16a34a"],
                ["T&C pending", 1, "#ea580c"],
              ]}
            />
            <div className="rows">
              {[
                ["AM", "Apex Manufacturing", "T&C signed", false, "#4f46e5"],
                ["BR", "BlueLeaf Retail", "T&C pending", true, "#0ea5e9"],
                ["NL", "Northwind Logistics", "T&C signed", false, "#059669"],
                ["SH", "Sunrise Healthcare", "T&C signed", false, "#ea580c"],
                ["GE", "Greenfield Education", "T&C signed", false, "#7c3aed"],
              ].map(([ini, name, tc, swap, c], n) => (
                <div
                  className="rowc rowc--sm"
                  key={name}
                  style={{ "--n": n, "--c": c }}
                >
                  <span className="rowc__av">{ini}</span>
                  <div className="rowc__main">
                    <b>{name}</b>
                    <small>{tc}</small>
                  </div>
                  {swap ? (
                    <Swap before="Accept invite" after="Active" tone="warn" />
                  ) : (
                    <Pill color="#16a34a">
                      <Check /> Active
                    </Pill>
                  )}
                </div>
              ))}
            </div>
          </Win>
        );
    }
  };

  return (
    <div className={`lg-page ${isVisible ? "lg-page--in" : ""}`}>
      {/* ---------- Top: software launcher ---------- */}
      <header className="lg-top">
        {(productsLoading || products.length > 0) && (
          <nav className="lg-apps__grid" aria-label="Your software">
            {productsLoading
              ? [0, 1, 2, 3].map((n) => (
                  <span
                    key={n}
                    className="lg-app lg-app--skeleton"
                    aria-hidden="true"
                  >
                    <span className="lg-app__icon" />
                    <i className="sk" style={{ "--w": "60%", margin: 0 }} />
                  </span>
                ))
              : products.map((p, i) => (
                  <a
                    key={p.productCode || p.productName}
                    href={p.productLink}
                    title={p.description || p.productName}
                    className="lg-app lg-reveal"
                    style={{
                      "--i": Math.min(i, 8),
                      "--c": p.color,
                      "--soft": p.softColor,
                    }}
                  >
                    <span className="lg-app__icon">
                      <ProductIcon product={p} />
                    </span>
                    <span className="lg-app__name">{p.productName}</span>
                    <ArrowUpRight className="lg-app__go w-4 h-4" />
                  </a>
                ))}
          </nav>
        )}
      </header>

      <main className="lg-main">
        {/* ---------- Left: sign in / reset ---------- */}
        <section className="lg-left lg-reveal" style={{ "--i": 4 }}>
          <div className="lg-left__body">
            <div className="lg-pane" key={activeTab}>
              {activeTab !== "forgot" ? (
                <>
                  <img
                    className="lg-brand"
                    src={ourDeptLogo}
                    alt="OurDept.com - One platform. Every department. Total control."
                  />
                  <h2>Welcome Back</h2>
                  <p className="lg-sub">Enter your credentials to continue</p>
                  {error && (
                    <div className="lg-msg bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="lg-msg bg-emerald-50 border border-emerald-200 text-emerald-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {success}
                    </div>
                  )}
                  <form className="lg-fields" onSubmit={handleSubmit}>
                    <InputField
                      label="Company ID"
                      type="text"
                      value={formData.tenantId}
                      onChange={(e) =>
                        handleInputChange("tenantId", e.target.value)
                      }
                      placeholder="Enter your company ID"
                      icon={Building}
                    />
                    <InputField
                      label="Username"
                      type="text"
                      value={formData.username}
                      onChange={(e) =>
                        handleInputChange("username", e.target.value)
                      }
                      placeholder="Enter your username"
                      icon={User}
                    />
                    <div className="space-y-2">
                      <label>Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) =>
                            handleInputChange("password", e.target.value)
                          }
                          className="w-full pl-3 pr-11 py-2.5 text-slate-800 placeholder-slate-400"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          aria-label="Toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    <button type="submit" disabled={loading} className="lg-btn">
                      {loading ? "Signing in..." : "Sign In"}
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={switchToForgotPassword}
                        className="lg-link"
                      >
                        Forgot your password?
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  {error && (
                    <div className="lg-msg bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="lg-msg bg-emerald-50 border border-emerald-200 text-emerald-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {success}
                    </div>
                  )}
                  <form className="lg-fields" onSubmit={handleForgotPassword}>
                    <div className="lg-row2">
                      <InputField
                        label="Company ID"
                        type="text"
                        value={formData.tenantId}
                        onChange={(e) =>
                          handleInputChange("tenantId", e.target.value)
                        }
                        placeholder="Company ID"
                        icon={Building}
                      />
                      <InputField
                        label="Email Address"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        placeholder="Email address"
                        icon={Mail}
                      />
                    </div>
                    <InputField
                      label="Date of Birth"
                      type="date"
                      value={formData.dob}
                      onChange={(e) => handleInputChange("dob", e.target.value)}
                      icon={Calendar}
                    />
                    <div className="lg-row2">
                      <div className="space-y-2">
                        <label>New Password</label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            value={formData.newPassword}
                            onChange={(e) =>
                              handleInputChange("newPassword", e.target.value)
                            }
                            className="w-full pl-3 pr-11 py-2.5 text-slate-800 placeholder-slate-400"
                            placeholder="New password"
                          />
                          <button
                            type="button"
                            aria-label="Toggle password visibility"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            {showNewPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label>Confirm Password</label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={(e) =>
                              handleInputChange(
                                "confirmPassword",
                                e.target.value,
                              )
                            }
                            className="w-full pl-3 pr-11 py-2.5 text-slate-800 placeholder-slate-400"
                            placeholder="Confirm password"
                          />
                          <button
                            type="button"
                            aria-label="Toggle password visibility"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <button type="submit" disabled={loading} className="lg-btn">
                      {loading ? "Processing..." : "Reset Password"}
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={switchToLogin}
                        className="lg-link inline-flex items-center"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back to Sign In
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
          {/* ---------- Footer: animated logo (under the left column only) ---------- */}
          <footer className="lg-foot">
            <a
              className="lg-logo"
              href="https://www.indialeadershipawards.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="India Leadership Awards"
              style={{ "--logo": `url(${ilaLogo})` }}
            >
              <span className="lg-logo__glow" aria-hidden="true" />
              <span className="lg-logo__art">
                <img src={ilaLogo} alt="India Leadership Awards" />
                <span className="lg-logo__shine" aria-hidden="true" />
                <span
                  className="lg-star"
                  style={{ "--x": "27%", "--y": "4%", "--d": "0s" }}
                />
                <span
                  className="lg-star"
                  style={{ "--x": "31.2%", "--y": "8%", "--d": "0.9s" }}
                />
                <span
                  className="lg-star"
                  style={{ "--x": "23.2%", "--y": "8%", "--d": "1.7s" }}
                />
              </span>
              {[8, 20, 32, 44, 56, 68, 80, 92].map((x, i) => (
                <span
                  key={x}
                  className="lg-spark"
                  style={{ "--x": `${x}%`, "--d": `${(i * 0.75) % 5}s` }}
                />
              ))}
            </a>
          </footer>
        </section>

        {/* ---------- Right: product preview ---------- */}
        <aside className="lg-right lg-reveal" style={{ "--i": 5 }}>
          {activeTab === "forgot" ? (
            <div className="lg-recover">
              <div className="lg-shield">
                <Shield className="w-10 h-10" />
              </div>
              <h1>Secure Recovery</h1>
              <p>
                We'll help you regain access to your account safely and securely
              </p>
              <div className="lg-note">
                Fill in all the required details to reset your password
                instantly.
              </div>
            </div>
          ) : (
            <>
              <div className="lg-slide" key={currentSlide}>
                <div className="lg-copy">
                  {currentSlides[currentSlide].badge && (
                    <span
                      className={`lg-badge lg-badge--${currentSlides[currentSlide].tone || "accent"}`}
                    >
                      {currentSlides[currentSlide].badge}
                    </span>
                  )}
                  <h2>{currentSlides[currentSlide].title}</h2>
                  <p className="lead">{currentSlides[currentSlide].content}</p>
                  {currentSlides[currentSlide].subtitle && (
                    <p>{currentSlides[currentSlide].subtitle}</p>
                  )}
                  {currentSlides[currentSlide].features && (
                    <ul className="lg-feats">
                      {currentSlides[currentSlide].features.map(
                        (feature, index) => (
                          <li key={index} style={{ "--n": index }}>
                            <Check />
                            {feature}
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                  {currentSlides[currentSlide].footer && (
                    <p className="lg-foot-note">
                      {currentSlides[currentSlide].footer}
                    </p>
                  )}
                </div>
                <div className="lg-winwrap">
                  {renderScene(currentSlides[currentSlide].visual)}
                </div>
              </div>
              <div className="lg-ctrl">
                <button
                  type="button"
                  className="lg-arrow"
                  onClick={prevSlide}
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="lg-dots">
                  {currentSlides.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      aria-label={`Go to slide ${index + 1}`}
                      onClick={() => setCurrentSlide(index)}
                      className={`lg-dot ${index === currentSlide ? "is-active" : ""}`}
                    >
                      <span />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="lg-arrow"
                  onClick={nextSlide}
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </aside>
      </main>
    </div>
  );
}
